// ===================================================Good Enough===============================================================


import React, { useState, useEffect } from 'react';
import { getFoodRecommendations } from '../features/recommendations/utils/foodRecommendations';
import { parseWeatherData } from '../features/weather/utils/weatherParser';
import { fetchScreenWeather } from '../features/weather/services/weatherApi';
import { WeatherSummary } from '../features/weather/components/WeatherSummary';
import { FoodCards } from '../features/recommendations/components/FoodCards';
// Right-hand column: rotating dish recommendation, the live weather-alert panel
// and the station footer. (Was AlertsPanel until 2026-07-16, then the floating
// InfoOverlay; both are superseded — the overlay shared the top-right corner
// with the carousel's cards and got clipped.)
import { InfoPanel } from '../features/alerts/components/InfoPanel';
import { MapboxIntro } from '../features/intro/components/MapboxIntro';
import { resolveTltLocation } from '../features/intro/data/tltLocations';
import { OverlayManager } from '../features/overlays/components/OverlayManager';
import { API_BASE, SCREEN_ID, SCREEN_KEY, hasScreenCredentials, pairingIsFromStorage, clearStoredPairing } from '../config';
import logoImage from '../assets/logos/WW_ALLWHITE_PORTRAIT.png';

// How often the display refreshes its nowcast. The backend caches station
// reads, so several screens polling at this rate stay cheap upstream.
const WEATHER_POLL_MS = 60 * 1000;
// Faster retry while the screen has no reading at all yet.
const WEATHER_RETRY_MS = 15 * 1000;

// Full-screen notice shown while the display can't render a location
// (not paired, pairing rejected, or first boot with the server unreachable).
function PairingNotice({ title, detail }) {
  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#0b0b19',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 18,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: 32,
      textAlign: 'center'
    }}>
      <img src={logoImage} alt="WeatherWalay" style={{ width: 'min(300px, 45vw)', height: 'auto', objectFit: 'contain', opacity: 0.9 }} />
      <div style={{ color: '#ffffff', fontSize: 26, fontWeight: 600, letterSpacing: 0.3 }}>{title}</div>
      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 15, maxWidth: 560, lineHeight: 1.65 }}>{detail}</div>
    </div>
  );
}

// Remember the last confirmed location so a reboot while the server is briefly
// unreachable still shows the right restaurant instead of an error screen.
const LOCATION_CACHE_KEY = 'ww_screen_location';

// Main App Component
export default function App() {
  const [weatherData, setWeatherData] = useState(null);
  const [foodRecommendations, setRecommendations] = useState([]);
  // The two closest weather stations for this screen (from the backend),
  // shown as points on the station map.
  const [weatherStations, setWeatherStations] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const [introFading, setIntroFading] = useState(false);
  const [screen, setScreen] = useState(null);
  const [screenLocation, setScreenLocation] = useState(null);
  // ok | unpaired | rejected | connecting | offline (offline = using cached location)
  const [pairingStatus, setPairingStatus] = useState('connecting');

  // Live nowcast from the nearest weather station, via the backend. A failed
  // poll keeps the last good reading on screen instead of blanking it.
  useEffect(() => {
    if (!hasScreenCredentials()) return undefined;

    let cancelled = false;
    let timer = null;
    let hasReading = false;

    async function pollWeather() {
      try {
        const { weather, stations } = await fetchScreenWeather();
        if (cancelled) return;

        hasReading = true;
        setWeatherData(parseWeatherData(weather));
        setRecommendations(getFoodRecommendations(weather));
        setWeatherStations(stations || []);
      } catch {
        // Keep showing the previous reading; just try again.
      }
      if (!cancelled) {
        timer = setTimeout(pollWeather, hasReading ? WEATHER_POLL_MS : WEATHER_RETRY_MS);
      }
    }

    pollWeather();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    async function loadScreenConfig() {
      if (!hasScreenCredentials()) {
        setPairingStatus('unpaired');
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/screens/${encodeURIComponent(SCREEN_ID)}/config`, {
          headers: { 'X-Screen-Key': SCREEN_KEY }
        });
        if (cancelled) return;

        if (res.status === 401 || res.status === 403) {
          // A stale runtime pairing (URL/localStorage) can shadow valid env
          // credentials, e.g. after a device-key rotation. Drop it and reload
          // once; won't loop since the pairing is gone after the reload.
          if (pairingIsFromStorage()) {
            clearStoredPairing();
            window.localStorage.removeItem(LOCATION_CACHE_KEY);
            window.location.reload();
            return;
          }
          setPairingStatus('rejected');
          return;
        }
        if (!res.ok) {
          throw new Error(`Unexpected response ${res.status}`);
        }

        const data = await res.json();
        if (cancelled) return;

        const locationSlug = data.screen.location || data.screen.name;
        window.localStorage.setItem(LOCATION_CACHE_KEY, locationSlug);
        setScreen(data.screen);
        setScreenLocation(resolveTltLocation(locationSlug));
        setPairingStatus('ok');
      } catch {
        if (cancelled) return;

        // Server unreachable. If this device has shown a location before, keep
        // displaying it; otherwise wait and retry until the server comes back.
        const cached = window.localStorage.getItem(LOCATION_CACHE_KEY);
        if (cached) {
          setScreenLocation((current) => current || resolveTltLocation(cached));
          setPairingStatus('offline');
        } else {
          setPairingStatus('connecting');
        }
        retryTimer = setTimeout(loadScreenConfig, 10000);
      }
    }

    loadScreenConfig();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, []);

  const handleIntroComplete = (nearestLocation) => {
    setIntroFading(true);
    setTimeout(() => {
      setShowIntro(false);
    }, 1000);
  };

  if (pairingStatus === 'unpaired') {
    return (
      <PairingNotice
        title="This display is not paired"
        detail={'Open the admin panel, select this screen’s location, and click “Get display URL”. Then open that URL on this device once — the pairing is remembered afterwards.'}
      />
    );
  }

  if (pairingStatus === 'rejected') {
    return (
      <PairingNotice
        title="Pairing is no longer valid"
        detail={'This display’s key was rejected by the server (it may have been rotated). Get a fresh display URL from the admin panel and open it on this device.'}
      />
    );
  }

  if (pairingStatus === 'connecting' && !screenLocation) {
    return (
      <PairingNotice
        title="Connecting…"
        detail="Waiting for the display server. Retrying automatically."
      />
    );
  }

  if (showIntro && !screenLocation) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#0b0b19'
      }} />
    );
  }

  if (showIntro) {
    return (
      <div style={{
        opacity: introFading ? 0 : 1,
        transition: 'opacity 1s ease-in-out'
      }}>
        <MapboxIntro targetLocation={screenLocation} onComplete={handleIntroComplete} />
      </div>
    );
  }

  if (!weatherData) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        fontSize: '24px'
      }}>
        Loading weather data...
      </div>
    );
  }

  return (
  <OverlayManager>
  <div style={{
    width: '100vw',
    height: '100vh',
    display: 'flex',
    position: 'relative',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflow: 'hidden',
    // background: '#0a0a0a'
    // background: 'radial-gradient(ellipse at 130% 50%, rgba(255,200,100,0.9) 0%, rgba(255,150,50,0.6) 25%, rgba(255,80,0,0.2) 50%, transparent 75%), linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
    // background: 'radial-gradient(circle at center, rgba(255,180,100,0.08), rgba(0,0,0,0.85)'
    // background: '#ffffff'
    // background: 'radial-gradient(ellipse at 130% 50%, rgba(100,180,255,0.9) 0%, rgba(80,150,255,0.6) 25%, rgba(50,100,200,0.2) 50%, transparent 75%), linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
    // background: 'radial-gradient(ellipse at 130% 50%, rgba(100,200,255,0.9) 0%, rgba(80,180,255,0.6) 25%, rgba(50,140,220,0.2) 50%, transparent 75%), linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
    background: 'radial-gradient(ellipse at 130% 50%, rgba(70,150,255,0.9) 0%, rgba(50,120,220,0.6) 25%, rgba(30,80,180,0.2) 50%, transparent 75%), linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)' 
  }}>

  <div style={{
      position: 'absolute',
      top: '0px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10,
      padding: '10px',
      pointerEvents: 'none', // prevents blocking interactions
    }}>
    <img
      src={logoImage}
      alt="WeatherWalay"
      style={{
        // Landscape mark (~1.92:1), so it's sized by width with the height
        // left to follow. The cap is the clear space above the carousel: its
        // cards are 500px tall and vertically centred, so the free strip at
        // the top is (100vh - 500)/2. Sizing against that (with a margin for
        // the tilted side cards) keeps the logo large on a 1080p+ display and
        // shrinks it on short screens instead of colliding with the cards.
        width: 'min(360px, 32vw)',
        height: 'auto',
        maxHeight: 'calc((100vh - 520px) / 2 - 24px)',
        objectFit: 'contain',
        opacity: 1,
      }}
    />
  </div>

      <WeatherSummary weatherData={weatherData} />
      <FoodCards foodRecommendations={foodRecommendations} />
      <InfoPanel
        stations={weatherStations}
        foodRecommendations={foodRecommendations}
      />
      {(screen || pairingStatus === 'offline') && (
        <div style={{
          position: 'absolute',
          left: 24,
          bottom: 18,
          zIndex: 12,
          color: pairingStatus === 'offline' ? '#fca5a5' : 'rgba(255,255,255,0.58)',
          fontSize: 13,
          letterSpacing: 0.2,
          background: 'rgba(0,0,0,0.28)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 8,
          padding: '8px 10px'
        }}>
          {pairingStatus === 'offline'
            ? `${screenLocation?.name || 'TLT'} · reconnecting to server…`
            : `${screen.name} · ${screenLocation?.name || screen.location || 'TLT'}`}
        </div>
      )}
    </div>
  </OverlayManager>
  );
}
